// Number-to-words and Roman numeral converters. Pure, browser-local.

export type NumberWordVariant =
  | 'number-to-words'
  | 'word-to-number-converter'
  | 'number-to-roman-numerals'
  | 'roman-numerals-to-number';

export const numberWordMeta: Record<
  NumberWordVariant,
  { badge: string; title: string; inputLabel: string; outputLabel: string; sample: string }
> = {
  'number-to-words': {
    badge: 'Words',
    title: 'Convert a number to words',
    inputLabel: 'Number',
    outputLabel: 'In words',
    sample: '1234',
  },
  'word-to-number-converter': {
    badge: 'Number',
    title: 'Convert words to a number',
    inputLabel: 'Number in words',
    outputLabel: 'Number',
    sample: 'one thousand two hundred thirty-four',
  },
  'number-to-roman-numerals': {
    badge: 'Roman',
    title: 'Convert a number to Roman numerals',
    inputLabel: 'Number (1 to 3999)',
    outputLabel: 'Roman numerals',
    sample: '2024',
  },
  'roman-numerals-to-number': {
    badge: 'Roman',
    title: 'Convert Roman numerals to a number',
    inputLabel: 'Roman numerals',
    outputLabel: 'Number',
    sample: 'MMXXIV',
  },
};

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const SCALES = [
  '',
  ' thousand',
  ' million',
  ' billion',
  ' trillion',
  ' quadrillion',
  ' quintillion',
];

function threeDigitsToWords(n: number): string {
  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) parts.push(`${ONES[hundreds]} hundred`);
  if (rest) {
    if (rest < 20) parts.push(ONES[rest]);
    else {
      const t = Math.floor(rest / 10);
      const o = rest % 10;
      parts.push(o ? `${TENS[t]}-${ONES[o]}` : TENS[t]);
    }
  }
  return parts.join(' ');
}

export function numberToWords(raw: string): string {
  const trimmed = raw.trim().replace(/,/g, '');
  if (!/^-?\d+$/.test(trimmed)) {
    throw new Error('Enter a whole number.');
  }
  const negative = trimmed.startsWith('-');
  let n = Math.abs(Number(trimmed));
  if (n === 0) return 'zero';
  if (!Number.isSafeInteger(n)) {
    throw new Error('Number is too large to convert.');
  }
  const groups: number[] = [];
  while (n > 0) {
    groups.push(n % 1000);
    n = Math.floor(n / 1000);
  }
  const words: string[] = [];
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    if (groups[i] === 0) continue;
    words.push(threeDigitsToWords(groups[i]) + SCALES[i]);
  }
  return (negative ? 'negative ' : '') + words.join(' ');
}

const ROMAN_MAP: Array<[number, string]> = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

export function numberToRoman(raw: string): string {
  const trimmed = raw.trim().replace(/,/g, '');
  if (!/^\d+$/.test(trimmed)) {
    throw new Error('Enter a whole number.');
  }
  let n = Number(trimmed);
  if (n < 1 || n > 3999) {
    throw new Error('Roman numerals cover 1 to 3999.');
  }
  let result = '';
  for (const [value, symbol] of ROMAN_MAP) {
    while (n >= value) {
      result += symbol;
      n -= value;
    }
  }
  return result;
}

export function romanToNumber(raw: string): string {
  const roman = raw.trim().toUpperCase();
  if (!/^[MDCLXVI]+$/.test(roman)) {
    throw new Error('Enter valid Roman numerals (M, D, C, L, X, V, I).');
  }
  const values: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let total = 0;
  for (let i = 0; i < roman.length; i += 1) {
    const current = values[roman[i]];
    const next = values[roman[i + 1]] ?? 0;
    total += current < next ? -current : current;
  }
  // Round-trip check catches malformed numerals like "IIII" or "VX".
  if (numberToRoman(String(total)) !== roman) {
    throw new Error('That is not a valid Roman numeral.');
  }
  return String(total);
}

const SMALL_WORDS: Record<string, number> = {};
ONES.forEach((word, i) => {
  SMALL_WORDS[word] = i;
});
TENS.forEach((word, i) => {
  if (word) SMALL_WORDS[word] = i * 10;
});
const SCALE_WORDS: Record<string, number> = {
  thousand: 1000,
  million: 1_000_000,
  billion: 1_000_000_000,
  trillion: 1_000_000_000_000,
};

export function wordsToNumber(raw: string): string {
  const cleaned = raw.trim().toLowerCase().replace(/,/g, '');
  if (!cleaned) return '';
  const negative = /^(negative|minus)\b/.test(cleaned);
  const tokens = cleaned
    .replace(/^(negative|minus)\b/, '')
    .replace(/\band\b/g, ' ')
    .split(/[\s-]+/)
    .filter(Boolean);
  if (!tokens.length) {
    throw new Error('Enter a number in words, like "two hundred".');
  }
  let result = 0;
  let current = 0;
  for (const token of tokens) {
    if (token in SMALL_WORDS) {
      current += SMALL_WORDS[token];
    } else if (token === 'hundred') {
      current = (current || 1) * 100;
    } else if (token in SCALE_WORDS) {
      result += (current || 1) * SCALE_WORDS[token];
      current = 0;
    } else {
      throw new Error(`Unrecognized word: "${token}".`);
    }
  }
  const total = result + current;
  return String(negative ? -total : total);
}

export function convertNumberWord(variant: NumberWordVariant, input: string): string {
  if (!input.trim()) return '';
  if (variant === 'number-to-words') return numberToWords(input);
  if (variant === 'word-to-number-converter') return wordsToNumber(input);
  if (variant === 'number-to-roman-numerals') return numberToRoman(input);
  return romanToNumber(input);
}
